import { Fragment, useEffect, useId, useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { FieldMarker } from "@/components/FieldMarker";
import { CarrierStepProblem } from "@/components/submissions/CarrierStepProblem";
import { CarrierDeclined } from "@/components/submissions/CarrierDeclined";
import { QuoteProgress } from "@/components/submissions/QuoteProgress";
import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
import { FIELD_ERROR } from "@/components/fieldStyles";
import {
  answerCarrierQuestions,
  carrierStepError,
  getCarrierQuestions,
  type AnswerSource,
  type CarrierAnswerValue,
  type CarrierQuestion,
  type CarrierQuestionSet,
  type CarrierDecline,
  type CarrierStepError,
  type MissingField,
  type QuoteAnswer,
} from "@/lib/submissions/brokerSubmissions";
import {
  fromApplication,
  placeQuestions,
  previousAnswerNote,
  questionHeadings,
  roundOfAnswers,
  roundToSave,
  savedSource,
  savedValues,
  unansweredRequired,
  yesNoOptions,
  type AnswerValues,
} from "@/lib/submissions/carrierQuestions";

// BRK-S290 — one carrier's questions, as the loop PMS runs them (PMS-S661):
// load the carrier's current set, save a round of answers, show any follow-ups
// that open (under the question that opened them, nested as deep as the
// carrier goes), and repeat until the set is complete; then hand over to the
// caller, which requests that carrier's quote. Answers are saved as the
// broker goes, so leaving and coming back resumes with them prefilled.
//
// Everything the carrier step can refuse renders here, in this carrier's place
// (CarrierStepError): missing insured details, answers that don't fit, the
// carrier's own refusal in its own words, a setup problem that is Signal's to
// fix, or the carrier being unreachable. Nothing is ever shown as done before
// the server says so (NN #7, the BRK-S122 class).
//
// A carrier that answers from the application (Great American, PMS-S705)
// leaves out what the application settles and pre-fills what it partly
// answers. A pre-fill says so until the broker changes it, and is sent back in
// the next round, since only then does it count. A round also carries what the
// application settles, so Continue saves an empty one when nothing is new but
// the carrier isn't complete, and a set with nothing to ask sends one by
// itself (BRK-S340).
//
// A saved answer that no longer fits its question comes back unanswered, the
// old value in `previousAnswer` (PMS-S744): the step says the earlier answer
// no longer exists, under the question (BRK-S344).
//
// One question per row at every width, each follow-up on the row under the
// question that opened it. The carrier decides how many questions there are,
// how long and how grouped, so no set of columns fits them (Nav, 2026-10-14;
// BRK-S352 undid BRK-S345's two columns here).

/** Indentation for a follow-up, by depth — capped so a deep chain still fits
 *  a phone. */
const DEPTH_INDENT = [
  "",
  "ml-3 border-l border-brk-border pl-3",
  "ml-6 border-l border-brk-border pl-3",
  "ml-9 border-l border-brk-border pl-3",
];

export function CarrierQuestions({
  submissionId,
  carrierId,
  carrier,
  onComplete,
  completeLabel = "Get a quote",
  onCompleteProfile,
  missingFieldHref,
  onDeclineChange,
  editDeclined = false,
}: {
  submissionId: string;
  carrierId: string;
  /** The carrier's display name, for this section's own messages. */
  carrier: string;
  /** Called once the set is complete: request this carrier's quote. A thrown
   *  error is read here, in this carrier's place. */
  onComplete: () => Promise<void>;
  completeLabel?: string;
  onCompleteProfile?: () => void;
  missingFieldHref?: (field: MissingField) => string | null;
  onDeclineChange?: (declined: CarrierDecline | undefined) => void;
  editDeclined?: boolean;
}) {
  const { authedRequest } = useAuth();
  const [questions, setQuestions] = useState<CarrierQuestion[] | null>(null);
  const [complete, setComplete] = useState(false);
  const [declined, setDeclined] = useState<CarrierDecline>();
  const [editing, setEditing] = useState(editDeclined);
  const [values, setValues] = useState<AnswerValues>({});
  const [source, setSource] = useState<AnswerSource>("user");
  const [phase, setPhase] = useState<"idle" | "saving" | "quoting">("idle");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<CarrierStepError | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // True while the step loads and carries on by itself (`start`).
  const [starting, setStarting] = useState(true);

  function take(set: CarrierQuestionSet) {
    setQuestions(set.questions);
    setComplete(set.complete);
    setValues(savedValues(set.questions));
    setDeclined(set.declined);
    onDeclineChange?.(set.declined);
  }

  async function finish() {
    setPhase("quoting");
    try {
      await onComplete();
    } catch (err) {
      const p = carrierStepError(err, "pricing");
      if (p.kind === "incomplete") {
        // The carrier asked something more since the last round: reload.
        await load();
        setNote(
          `${carrier} has a few more questions — answer them to continue.`,
        );
      } else if (p.kind === "fieldErrors") {
        setFieldErrors(p.fieldErrors);
        setNote(
          "Your answers are saved. Check the marked questions and Continue.",
        );
      } else {
        setProblem(p);
      }
    }
  }

  async function load() {
    setProblem(null);
    try {
      const set = await getCarrierQuestions(
        authedRequest,
        submissionId,
        carrierId,
      );
      take(set);
      setSource(savedSource(set.questions));
      return set;
    } catch (err) {
      setProblem(carrierStepError(err));
      return null;
    }
  }

  /** Saves a round and takes the set that comes back; null when PMS refused
   *  it, which shows in this carrier's place. */
  async function saveRound(round: QuoteAnswer[]) {
    setPhase("saving");
    try {
      const set = await answerCarrierQuestions(
        authedRequest,
        submissionId,
        carrierId,
        round,
      );
      take(set);
      if (set.declined) setEditing(false);
      return set;
    } catch (err) {
      const p = carrierStepError(err);
      if (p.kind === "fieldErrors") {
        setFieldErrors(p.fieldErrors);
        setNote(
          "The answers that fit were saved. Check the marked questions and Continue.",
        );
      } else {
        if (p.kind === "rejected" && p.set) take(p.set);
        setProblem(p);
      }
      return null;
    } finally {
      setPhase("idle");
    }
  }

  /** Loads the set and, with nothing to ask, carries on by itself: an empty
   *  round while the carrier isn't complete (PMS-S705), then the quote. */
  async function start(cancelled: () => boolean = () => false) {
    setStarting(true);
    try {
      let set = await load();
      if (
        !cancelled() &&
        set &&
        !set.declined &&
        !set.complete &&
        set.questions.length === 0
      ) {
        set = await saveRound([]);
      }
      // Nothing to answer: straight to the quote, no empty form (BRK-S054).
      if (
        !cancelled() &&
        set &&
        !set.declined &&
        set.complete &&
        set.questions.length === 0
      ) {
        await finish();
        if (!cancelled()) setPhase("idle");
      }
    } finally {
      if (!cancelled()) setStarting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    void start(() => cancelled);
    return () => {
      cancelled = true;
    };
    // Load once per carrier; `start` reads the latest props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, carrierId]);

  function setValue(id: string, value: CarrierAnswerValue) {
    setValues((v) => ({ ...v, [id]: value }));
    setFieldErrors((errors) => {
      const next = { ...errors };
      delete next[id];
      return next;
    });
    setNote(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!questions) return;
    setProblem(null);
    setNote(null);
    const missing = unansweredRequired(questions, values);
    if (missing.length > 0) {
      setNote(`Answer “${missing[0].prompt}” to continue.`);
      return;
    }
    const round = roundToSave(questions, values, source, complete);
    let done = complete;
    if (round !== null) {
      const before = new Set(questions.map((q) => q.id));
      const set = await saveRound(round);
      if (!set) return;
      if (set.declined) return;
      done = set.complete;
      if (!done && set.questions.some((q) => !before.has(q.id))) {
        setNote("Your answers opened a few more questions.");
      } else if (!done && round.length === 0) {
        setNote("Answer the remaining questions to continue.");
      }
    }
    if (done && (round !== null || !declined)) {
      await finish();
      setPhase("idle");
    }
  }

  const busy = phase !== "idle";
  const unsaved = questions
    ? roundOfAnswers(questions, values, source).length > 0
    : false;

  // The quote's stages stand in for the form while the carrier's
  // questions load (with the round a carrier with nothing to ask sends by
  // itself) and while it prices (BRK-S369). The component stays mounted, so
  // a refusal brings the form back with the answers as they were.
  if (phase === "quoting")
    return (
      <QuoteProgress
        carrier={carrier}
        at={2}
        nothingToAnswer={questions !== null && questions.length === 0}
      />
    );
  if (starting && !problem) return <QuoteProgress carrier={carrier} at={0} />;
  if (declined && !editing)
    return (
      <div className="flex flex-col gap-3">
        <CarrierDeclined carrier={carrier} reasons={declined.reasons} />
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`${buttonClass({ size: "sm" })} self-start`}
        >
          Change answers
        </button>
      </div>
    );

  return (
    <div className="flex flex-col gap-3">
      {problem && (
        <CarrierStepProblem
          problem={problem}
          carrier={carrier}
          onCompleteProfile={onCompleteProfile}
          missingFieldHref={missingFieldHref}
          onRetry={
            questions === null || questions.length === 0
              ? () => void start()
              : undefined
          }
        />
      )}
      {questions !== null && questions.length === 0 && complete && !problem && (
        <p className="text-sm text-brk-dim">Nothing to answer.</p>
      )}
      {questions !== null &&
        questions.length === 0 &&
        !complete &&
        (phase === "saving" ? (
          <p role="status" className="text-sm text-brk-dim">
            Sending {carrier} what the application answers…
          </p>
        ) : (
          !problem &&
          !starting && (
            <div role="status" className="flex flex-col gap-1 text-sm">
              <span className="text-brk-dim">
                {carrier} has nothing more to ask, but isn’t ready to quote yet.
              </span>
              <button
                type="button"
                onClick={() => void start()}
                className="self-start text-brk-accent hover:underline"
              >
                Try again
              </button>
            </div>
          )
        ))}
      {questions !== null && questions.length > 0 && (
        <form
          className="flex flex-col gap-3"
          noValidate
          onSubmit={handleSubmit}
        >
          <CarrierQuestionFields
            questions={questions}
            values={values}
            onChange={setValue}
            source={source}
            onSourceChange={setSource}
            errors={fieldErrors}
            disabled={busy}
          />

          {note && (
            <p role="status" className="text-sm text-brk-dim">
              {note}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className={`${buttonClass({ size: "sm" })} self-start`}
          >
            {phase === "saving"
              ? "Saving…"
              : complete && !unsaved
                ? completeLabel
                : "Continue"}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * A carrier's questions as fields (BRK-S290): who is answering, then each
 * question that shows, its group's heading above it and each follow-up on
 * the row under the question that opened it. The popup's loop and the WC
 * wizard's last step (BRK-S393) both render it; each keeps its own answers.
 */
export function CarrierQuestionFields({
  questions,
  values,
  onChange,
  source,
  onSourceChange,
  errors,
  disabled,
}: {
  questions: CarrierQuestion[];
  values: AnswerValues;
  onChange: (questionId: string, value: CarrierAnswerValue) => void;
  source: AnswerSource;
  onSourceChange: (source: AnswerSource) => void;
  /** Each question's error, by question id. */
  errors: Readonly<Record<string, string>>;
  disabled: boolean;
}) {
  const sourceName = useId();
  const placed = placeQuestions(questions, values);
  const headings = questionHeadings(placed);
  return (
    <>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium text-brk-text">
          Who is answering?
        </legend>
        <div className="flex flex-col gap-1 sm:flex-row sm:gap-x-4">
          {(
            [
              ["user", "Me"],
              ["insured", "The insured — I’m entering their answers"],
            ] as const
          ).map(([value, label]) => (
            <label
              key={value}
              className={`flex items-center gap-2 text-sm text-brk-text ${TOUCH_TARGET}`}
            >
              <input
                type="radio"
                name={sourceName}
                checked={source === value}
                onChange={() => onSourceChange(value)}
                disabled={disabled}
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {placed.map(({ question, depth }, i) => (
        <Fragment key={question.id}>
          {headings[i] && (
            <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-brk-dim">
              {headings[i]}
            </p>
          )}
          <div
            data-question={question.id}
            className={DEPTH_INDENT[Math.min(depth, 3)]}
          >
            <QuestionControl
              question={question}
              value={values[question.id]}
              onChange={(v) => onChange(question.id, v)}
              error={errors[question.id]}
              disabled={disabled}
            />
            {fromApplication(question, values[question.id]) && (
              <p className="mt-1 text-xs text-brk-dim">
                Filled in from the application. Check it.
              </p>
            )}
            {previousAnswerNote(question) && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {previousAnswerNote(question)}
              </p>
            )}
          </div>
        </Fragment>
      ))}
    </>
  );
}

function QuestionControl({
  question,
  value,
  onChange,
  error,
  disabled,
}: {
  question: CarrierQuestion;
  value: CarrierAnswerValue | undefined;
  onChange: (value: CarrierAnswerValue) => void;
  error?: string;
  disabled?: boolean;
}) {
  const text = typeof value === "string" ? value : "";
  switch (question.type) {
    case "boolean":
    case "select":
      return (
        <SelectField
          label={question.prompt}
          placeholder="Select…"
          options={
            question.type === "boolean"
              ? yesNoOptions(question)
              : (question.options ?? []).map((o) => ({ value: o, label: o }))
          }
          value={text}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          error={error}
          disabled={disabled}
        />
      );
    case "multi_select":
      return (
        <MultiSelectQuestion
          question={question}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          error={error}
          disabled={disabled}
        />
      );
    case "number":
      return (
        <TextField
          label={question.prompt}
          type="number"
          inputMode="decimal"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          error={error}
          disabled={disabled}
        />
      );
    default:
      // `text`, and any type a newer PMS adds: answered as text, never a crash.
      return (
        <TextField
          label={question.prompt}
          type="text"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          error={error}
          disabled={disabled}
        />
      );
  }
}

// A multi-select answered as a checkbox group; the answer is the list of the
// ticked option labels, in the carrier's order. Options combine freely — the
// contract marks no option exclusive ("None of the above" included), and the
// carrier's own check is the authority.
function MultiSelectQuestion({
  question,
  value,
  onChange,
  error,
  disabled,
}: {
  question: CarrierQuestion;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
}) {
  const options = question.options ?? [];
  const errorId = useId();
  function toggle(option: string) {
    onChange(
      value.includes(option)
        ? value.filter((v) => v !== option)
        : options.filter((o) => value.includes(o) || o === option),
    );
  }
  return (
    <fieldset
      className="flex flex-col gap-1.5"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
    >
      <legend className="flex items-baseline gap-1.5 text-sm font-medium text-brk-text">
        {question.prompt}
        <FieldMarker required={question.required} />
      </legend>
      <div className="flex flex-col gap-1.5">
        {options.map((o) => (
          <label
            key={o}
            className={`flex items-center gap-2 text-sm text-brk-text ${TOUCH_TARGET}`}
          >
            <input
              type="checkbox"
              checked={value.includes(o)}
              onChange={() => toggle(o)}
              disabled={disabled}
            />
            {o}
          </label>
        ))}
      </div>
      {error && (
        <p id={errorId} className={FIELD_ERROR}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
